import { Link } from "react-router-dom";
import { Check, ShieldCheck, Radio, Coins, Gift } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { SectionHead } from "@/components/landing/HowItWorks";
import { PLATFORMS } from "@/components/landing/data";

export const Platforms = () => {
  const { t } = useI18n();
  return (
    <section id="platforms" className="py-20 scroll-mt-20 border-y border-white/[0.05] bg-[#0b0e1a]/60" data-testid="platforms-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col lg:flex-row lg:items-center gap-10">
        <div className="lg:w-80 shrink-0"><div className="cx-label">{t("ld.pf_label")}</div><h2 className="font-display text-2xl sm:text-3xl font-bold text-white mt-3">{t("ld.pf_title")}</h2></div>
        <div className="flex flex-wrap gap-3">
          {PLATFORMS.map(([id, name, c]) => (
            <div key={id} data-testid={`platform-${id}`} className="group flex items-center gap-3 rounded-2xl px-5 py-4 border border-white/[0.08] bg-[#0e1222] transition-colors hover:border-white/20">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: c, boxShadow: `0 0 14px ${c}` }} />
              <span className="font-display text-lg font-semibold text-white">{name}</span>
              {id === "obs" && <span className="text-[10px] font-mono text-slate-500 flex items-center gap-1"><Radio className="w-3 h-3" />{t("ld.pf_obs")}</span>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FEATURES = ["ld.free_f1", "ld.free_f2", "ld.free_f3", "ld.free_f4", "ld.free_f5"];

const PERKS = [
  { id: "credits", Icon: Coins, c: "#3ad7ff" },
  { id: "referral", Icon: Gift, c: "#f472b6" },
];

const Perk = ({ id, Icon, c }) => {
  const { t } = useI18n();
  return (
    <div className="cx-perk flex gap-4 rounded-2xl p-5" style={{ "--c": c }} data-testid={`free-perk-${id}`}>
      <span className="grid place-items-center w-11 h-11 rounded-xl shrink-0" style={{ background: `${c}1f`, color: c, boxShadow: `0 0 18px ${c}33` }}><Icon className="w-5 h-5" /></span>
      <div>
        <div className="font-display text-base font-bold text-white">{t(`ld.perk_${id}_t`)}</div>
        <p className="text-sm text-slate-400 mt-1 leading-relaxed">{t(`ld.perk_${id}_d`)}</p>
        <div className="flex flex-wrap gap-1.5 mt-3">{[1, 2, 3].map((n) => <span key={n} className="rounded-full px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-wider border" style={{ color: c, borderColor: `${c}55`, background: `${c}10` }}>{t(`ld.perk_${id}_${n}`)}</span>)}</div>
      </div>
    </div>
  );
};

export const FreePlan = () => {
  const { t } = useI18n();
  return (
    <section id="free" className="relative py-24 scroll-mt-20" data-testid="free-section">
      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[520px] h-[420px] bg-[#a746ff]/[0.07] blur-[130px] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <SectionHead label={t("ld.free_label")} title={t("ld.free_title")} sub={t("ld.free_sub")} />
          <div className="space-y-4 -mt-4">{PERKS.map((p) => <Perk key={p.id} {...p} />)}</div>
        </div>
        <div className="cx-neon-frame rounded-3xl p-[1.5px]" data-testid="free-plan-card">
        <div className="relative rounded-3xl p-8 sm:p-10 overflow-hidden bg-[#0c0d1c]">
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#a746ff]/30 blur-[90px] pointer-events-none" />
          <div className="absolute -bottom-28 -left-20 w-64 h-64 rounded-full bg-[#2f7bff]/20 blur-[90px] pointer-events-none" />
          <span className="absolute top-6 right-6 rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-[0.2em] text-white bg-gradient-to-r from-[#a746ff] to-[#2f7bff] shadow-[0_0_18px_rgba(167,70,255,.5)]" data-testid="free-plan-badge">{t("ld.free_badge")}</span>
          <div className="relative flex items-end gap-3">
            <span className="cx-big-3 font-display text-8xl font-black cx-text-grad leading-none">3</span>
            <span className="text-slate-300 pb-2">{t("ld.per_week")}</span>
          </div>
          <ul className="relative mt-8 space-y-3">
            {FEATURES.map((k) => <li key={k} className="flex items-start gap-3 text-sm text-slate-300"><span className="grid place-items-center w-5 h-5 rounded-full bg-[#a746ff]/20 shrink-0"><Check className="w-3 h-3 text-[#e9c6ff]" /></span>{t(k)}</li>)}
          </ul>
          <Link to="/login?mode=register" data-testid="free-plan-cta" className="relative mt-9 cx-btn rounded-full w-full py-3.5 font-semibold flex justify-center">{t("ld.free_cta")}</Link>
          <div className="relative mt-5 flex flex-wrap items-center justify-between gap-3 text-xs">
            <span className="text-slate-500">{t("ld.free_soon")}</span>
            <span className="flex items-center gap-1.5 text-[#3ad7ff]" data-testid="law-25-badge"><ShieldCheck className="w-4 h-4" />{t("ld.law25")}</span>
          </div>
        </div>
        </div>
      </div>
    </section>
  );
};
